import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DashboardLayout } from "./components/DashboardLayout";
import { ErrorBoundary } from "./components/ErrorBoundary";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Activate from "./pages/Activate";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PublicEvent from "./pages/PublicEvent";
import PublicEvents from "./pages/PublicEvents";
import PublicOrganiser from "./pages/PublicOrganiser";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import TicketView from "./pages/TicketView";
import ResendTickets from "./pages/ResendTickets";
import ScannerPage from "./pages/ScannerPage";
import Privacy from "./pages/Privacy";
import FeedbackPage from "./pages/Feedback";
import UnsubscribePage from "./pages/Unsubscribe";
import WaitlistCancelPage from "./pages/WaitlistCancel";
import NotFound from "./pages/NotFound";
import Pricing from "./pages/Pricing";
import WhoWeAre from "./pages/WhoWeAre";

import MyEvents from "./pages/dashboard/MyEvents";
import EventForm from "./pages/dashboard/EventForm";
import Venues from "./pages/dashboard/Venues";
import VenueForm from "./pages/dashboard/VenueForm";
import VenueDetail from "./pages/dashboard/VenueDetail";
import LayoutDesigner from "./pages/dashboard/LayoutDesigner";
import LayoutCanvasDesigner from "./pages/dashboard/LayoutCanvasDesigner";
import ScannerTokens from "./pages/dashboard/ScannerTokens";
import Reports from "./pages/dashboard/Reports";
import AdminPage from "./pages/dashboard/AdminPage";
import Profile from "./pages/dashboard/Profile";
import SalesDashboard from "./pages/dashboard/SalesDashboard";
import AttendeesDashboard from "./pages/dashboard/AttendeesDashboard";
import StaffOrderView from "./pages/dashboard/StaffOrderView";
import AdminOrderDetail from "./pages/dashboard/AdminOrderDetail";
import AdminTicketDetail from "./pages/dashboard/AdminTicketDetail";
import PayoutsDashboard from "./pages/dashboard/PayoutsDashboard";
import CookieConsent from "./components/CookieConsent";
import TeamInviteAccept from "./pages/TeamInviteAccept";
import EventTeam from "./pages/dashboard/EventTeam";

// Reset the top-level ErrorBoundary whenever the pathname changes so users
// aren't trapped on a crashed screen after navigating away.
function RoutedErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors closeButton />
          <RoutedErrorBoundary>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/activate" element={<Activate />} />
              <Route path="/team-invite" element={<TeamInviteAccept />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              {/* Public event pages */}
              <Route path="/events" element={<PublicEvents />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/who-we-are" element={<WhoWeAre />} />
              <Route path="/e/:slug" element={<PublicEvent mode="public" />} />
              <Route path="/e/private/:key" element={<PublicEvent mode="private" />} />
              <Route path="/o/:slug" element={<PublicOrganiser />} />

              {/* Guest checkout (no auth) */}
              <Route path="/checkout/:sessionId" element={<Checkout />} />
              <Route path="/order/:orderId" element={<OrderConfirmation />} />

              {/* Hosted ticket + resend (no auth) */}
              <Route path="/t/:code" element={<TicketView />} />
              <Route path="/resend" element={<ResendTickets />} />

              {/* Door scanner (authorised by scanner token in URL) */}
              <Route path="/scan/:token" element={<ScannerPage />} />

              {/* Privacy notice (public) */}
              <Route path="/privacy" element={<Privacy />} />

              {/* Sprint V — post-event feedback + follow-up opt-out (public) */}
              <Route path="/feedback/:token" element={<FeedbackPage />} />
              <Route path="/unsubscribe" element={<UnsubscribePage />} />
              <Route path="/waitlist/cancel" element={<WaitlistCancelPage />} />

              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard/events" replace />} />
                <Route path="events" element={<MyEvents />} />
                <Route path="events/new" element={<EventForm />} />
                <Route path="events/:id/edit" element={<EventForm />} />
                <Route path="events/:id/scanners" element={<ScannerTokens />} />
                <Route path="events/:id/reports" element={<Reports />} />
                <Route path="events/:id/team" element={<EventTeam />} />
                <Route path="venues" element={<Venues />} />
                <Route path="venues/new" element={<VenueForm />} />
                <Route path="venues/:id" element={<VenueDetail />} />
                <Route path="venues/:id/edit" element={<VenueForm />} />
                <Route path="venues/:id/templates/:tid/layout" element={<LayoutDesigner />} />
                <Route path="venues/:venueId/templates/:templateId/designer" element={<LayoutCanvasDesigner />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/orders/:orderId" element={<AdminOrderDetail />} />
                <Route path="admin/tickets/:ticketId" element={<AdminTicketDetail />} />
                <Route path="orders/:orderId" element={<StaffOrderView />} />
                <Route path="sales" element={<SalesDashboard />} />
                <Route path="attendees" element={<AttendeesDashboard />} />
                <Route path="payouts" element={<PayoutsDashboard />} />
                <Route path="profile" element={<Profile />} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </RoutedErrorBoundary>
          <CookieConsent />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
